import React, { useState, useMemo } from "react";
import { useAppDispatch } from "../../../app/hooks";

import ManagementModal from "../../../components/ui/modals/ManagementModal";
import AssetSelectionStep from "./AssetSelectionStep";

import LoanSelectionStep from "./LoanSelectionStep";
import ReviewChangesModal from "./ReviewChangesModal";

import {
  ChargeFields,
  PublicChargeRegistry,
  ChargeWithLinkages,
  CreateChargeLoanLinkageFields,
  CreateChargeAssetLinkageFields,
} from "../../../api/charges";
import { createCharge } from "../Logic/chargesThunk";
import ChargeInformationStep from "./ChargeDetailStep";
import { useParams } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import Stepper, { Step } from "../../../components/ui/stepper/Stepper";
import AddAssetModal from "./AddAssetModal";
import { Plus } from "lucide-react";

const ADD_CHARGE_STEPS: Step[] = [
  { id: 1, label: "Charge Details" },
  { id: 2, label: "Assets" },
  { id: 3, label: "Loans" },
];

interface AddChargeModalProps {
  onClose: () => void;
  onBack: () => void;
}

const AddChargeModal: React.FC<AddChargeModalProps> = ({
  onClose,
  onBack,
}) => {
  const dispatch = useAppDispatch();

  const { id: caseId } = useParams<{ id: string }>();
  
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAddAssetOpen, setIsAddAssetOpen] = useState<boolean>(false);

  const [selectedAssetLinkages, setSelectedAssetLinkages] = useState<CreateChargeAssetLinkageFields[]>([]);
  const [selectedLoanLinkages, setSelectedLoanLinkages] = useState<CreateChargeLoanLinkageFields[]>([]);
  
  const [chargeInformation, setChargeInformation] = useState<ChargeFields>({
    security_role: undefined,
    charge_mode: undefined,
    distribution_rights: undefined,
    public_registry: PublicChargeRegistry.CERSAI,
    registry_identifiers: {
      security_interest_id: "",
    },
  });

  const isAssetsStepValid = selectedAssetLinkages.length > 0;

  const isChargeInfoStepValid = useMemo(() => {
    const hasRequiredFields =
      !!chargeInformation.security_role &&
      !!chargeInformation.charge_mode &&
      !!chargeInformation.distribution_rights &&
      !!chargeInformation.public_registry;

    if (chargeInformation.public_registry === PublicChargeRegistry.CERSAI) {
      const securityId = chargeInformation.registry_identifiers?.security_interest_id;
      return hasRequiredFields && !!securityId && securityId.trim().length > 0;
    }

    return hasRequiredFields;
  }, [chargeInformation]);

  const isLoansStepValid = selectedLoanLinkages.length > 0;

  const isNextEnabled = useMemo(() => {
    switch (currentStep) {
      case 1:
        return isChargeInfoStepValid;
      case 2:
        return isAssetsStepValid;
      case 3:
        return isLoansStepValid;
      default:
        return false;
    }
  }, [currentStep, isAssetsStepValid, isChargeInfoStepValid, isLoansStepValid]);

  const handleBack = () => {
    if (currentStep === 1) {
      onBack();
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (isNextEnabled) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleConfirmSave = async () => {
    setIsLoading(true);

    try {
      await dispatch(
        createCharge({
          case_id: caseId!,
          request: {
            charge: chargeInformation,
            asset_linkages: selectedAssetLinkages,
            loan_linkages: selectedLoanLinkages,
          },
        }),
      ).unwrap();

      showSuccessToast("Charge added successfully.");

      onClose();
    } catch (error) {
      showErrorToast("Failed to add charge.");
    } finally {
      setIsLoading(false);
    }
  };

  const constructedUpdatedCharge = useMemo<ChargeWithLinkages>(() => {
    return {
      charges: {
        charge_id: "temp-new-charge",
        case_id: caseId ?? "",
        ...chargeInformation,
      },
      asset_linkages: selectedAssetLinkages.map((item) => ({
        linkage_id: `temp-asset-${item.asset_id}`,
        asset_id: item.asset_id,
        charge_id: "temp-new-charge",
      })),
      loan_linkages: selectedLoanLinkages.map((item) => ({
        linkage_id: `temp-loan-${item.loan_reference_id}`,
        loan_reference_id: item.loan_reference_id,
        charge_id: "temp-new-charge",
      })),
    };
  }, [chargeInformation, selectedAssetLinkages, selectedLoanLinkages, caseId]);

  if (isAddAssetOpen) {
    return (
      <AddAssetModal
        onClose={() => setIsAddAssetOpen(false)}
        onBack={() => setIsAddAssetOpen(false)}
        onSuccess={(newAssetId) => {
          setSelectedAssetLinkages((prev) => [
            ...prev,
            { asset_id: newAssetId },
          ]);
        }}
      />
    );
  }

  if (currentStep === 4) {
    return (
      <ReviewChangesModal
        updatedCharge={constructedUpdatedCharge}
        onBack={() => setCurrentStep(3)}
        onClose={onClose}
        onConfirm={handleConfirmSave}
        isLoading={isLoading}
      />
    );
  }

  return (
    <ManagementModal
      title="Add Charge"
      onClose={onClose}
      onBack={handleBack}
      onSubmit={handleNext}
      submitLabel="Next"
      submitEnabled={isNextEnabled}
      isLoading={isLoading}
    >
      <Stepper currentStep={currentStep} steps={ADD_CHARGE_STEPS} />

      <div className="mt-4 flex flex-col gap-4">
        {currentStep === 1 && (
          <ChargeInformationStep
            chargeInformation={chargeInformation}
            onChange={(updates) =>
              setChargeInformation((prev) => ({
                ...prev,
                ...updates,
              }))
            }
          />
        )}

        {currentStep === 2 && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-[14px] font-medium leading-5 text-[#0A0A0A]">
                Select assets
              </span>

              <button
                onClick={() => setIsAddAssetOpen(true)}
                className="flex items-center gap-2 text-[14px] font-medium leading-5 text-[#3551F3]"
              >
                <Plus size={16} />
                <span>Add New Asset</span>
              </button>
            </div>

            <AssetSelectionStep
              selectedAssetIds={selectedAssetLinkages}
              onSelectionChange={setSelectedAssetLinkages}
            />
          </>
        )}

        {currentStep === 3 && (
          <>
            <span className="text-[14px] font-medium leading-5 text-[#0A0A0A]">
              Select loans
            </span>

            <LoanSelectionStep
              selectedLoanIds={selectedLoanLinkages}
              onSelectionChange={setSelectedLoanLinkages}
            />
          </>
        )}
      </div>
    </ManagementModal>
  );
};

export default AddChargeModal;